import { EFinancingProgram } from '@if/core/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  EditPersonalLoanWorksheetDto,
  EditWorksheetPaymentSplitDto,
  FullCreditApplication,
  PersonalLoanWorksheet,
  PersonalWorksheetDecisionDTO,
  PersonalWorksheetRevision,
} from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const baseUrl = '/Applications/personal/{creditApplicationId}/Worksheet'

// Query Keys
export const personalLoanWorksheetKeys = {
  all: ['personalLoanWorksheet'] as const,
  revisions: (creditApplicationId: string) =>
    [...personalLoanWorksheetKeys.all, 'revisions', creditApplicationId] as const,
}

// API Functions
export async function createPersonalLoanWorksheet(data: EditPersonalLoanWorksheetDto): Promise<PersonalLoanWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.post<PersonalLoanWorksheet>(url, data)
  return response.data
}

export async function updatePersonalLoanWorksheet(data: EditPersonalLoanWorksheetDto): Promise<PersonalLoanWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<PersonalLoanWorksheet>(url, data)
  return response.data
}

export async function computePersonalLoanFunding(data: FundingInfoDto): Promise<FundingComputedDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<FundingComputedDto>('Applications/ComputeFunding', data)
  return response.data
}

export async function updatePersonalLoanWorksheetDecision(
  data: PersonalWorksheetDecisionDTO,
): Promise<PersonalLoanWorksheet> {
  const apiClient = getApiClient()
  const url = `${baseUrl}/Decision`.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<PersonalLoanWorksheet>(url, data)
  return response.data
}

export async function getPersonalLoanWorksheetRevisions(
  creditApplicationId: string,
): Promise<PersonalWorksheetRevision[]> {
  const apiClient = getApiClient()
  const url = `${baseUrl}/Revisions`.replace('{creditApplicationId}', creditApplicationId)
  const response = await apiClient.get<PersonalWorksheetRevision[]>(url)
  return response.data
}

// React-Query Hooks
export function useGetPersonalLoanWorksheetRevisions(
  creditApplicationId: string | undefined,
): [PersonalWorksheetRevision[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: personalLoanWorksheetKeys.revisions(creditApplicationId ?? ''),
    queryFn: () => getPersonalLoanWorksheetRevisions(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}

export function useCreatePersonalLoanWorksheet(): [
  MutationFunction<PersonalLoanWorksheet, EditPersonalLoanWorksheetDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createPersonalLoanWorksheet,
    onSuccess: (createdWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(createdWorksheet.creditApplicationId, EFinancingProgram.Personal)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdatePersonalLoanWorksheet(): [
  MutationFunction<PersonalLoanWorksheet, EditPersonalLoanWorksheetDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updatePersonalLoanWorksheet,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.Personal)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useComputePersonalLoanFunding(): [MutationFunction<FundingComputedDto, FundingInfoDto>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: computePersonalLoanFunding,
  })

  return [mutateAsync, isPending]
}

export function useUpdatePersonalLoanWorksheetDecision(): [
  MutationFunction<PersonalLoanWorksheet, PersonalWorksheetDecisionDTO>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updatePersonalLoanWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.Personal)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Existing hook for finalize high estimate (kept as-is, updated cache invalidation)
const completeFinalizeHighEstimate: MutationFunction<
  FullCreditApplication,
  { dto: EditWorksheetPaymentSplitDto; creditApplicationId: string }
> = async ({ dto, creditApplicationId }) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<FullCreditApplication>(
    `/Applications/personal/${creditApplicationId}/Worksheet/FinalizeHighEstimate`,
    dto,
  )
  return response.data
}

export function useCompleteFinalizeHighEstimate(): [
  MutationFunction<FullCreditApplication, { dto: EditWorksheetPaymentSplitDto; creditApplicationId: string }>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { isPending, mutateAsync } = useMutation<
    FullCreditApplication,
    Error,
    { dto: EditWorksheetPaymentSplitDto; creditApplicationId: string }
  >({
    mutationFn: completeFinalizeHighEstimate,
    onSuccess: (fullCreditApp, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, EFinancingProgram.Personal)
      queryClient.setQueryData<FullCreditApplication>(queryKey, fullCreditApp)
    },
  })

  return [mutateAsync, isPending]
}
